import { AfterViewInit, Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ReportGeneratorStore } from './report-generator.store';
import { Observable, take } from 'rxjs';
import { ReportGeneratorService } from './report-generator.service';
import { ReportConfig, ReportTemplate } from './interfaces';
import { ActivatedRoute } from '@angular/router';
import { ActiveTemplateLocalStorageKey, DEFAULT_ZOOM, PortaitPreview } from './constants';
import { LibraryPage } from './interfaces';
import { createState } from '@shared/helpers/create-state.helper';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogModel } from '@shared/dialogs/confirm-dialog/confirm-dialog.component';
import { AddPageDialogComponent } from './components/shared/add-page-dialog/add-page-dialog.component';
import { MixpanelService } from '@core/services/mixpanel.service';
import { SessionService } from '@core/services/session.service';

const LOCAL_STORAGE_ZOOM_KEY = 'report-generator-zoom';

@Component({
  selector: 'app-report-generator',
  templateUrl: './report-generator.component.html',
  styleUrls: ['./report-generator.component.scss'],
})
export class ReportGeneratorComponent implements OnInit, AfterViewInit {
  @ViewChild('centerColumn') centerColumn: ElementRef;
  @ViewChildren('pageWrapper') pageWrappers: QueryList<ElementRef>;

  config$: Observable<ReportConfig | null>;
  templates$: Observable<ReportTemplate[]>;
  pageLibrary$: Observable<LibraryPage[]>;

  selectedPageIndex: number = -1;

  pageState = createState<'preview' | 'configure'>('preview');
  isConfiguring = createState<boolean>(false);

  constructor(
    private readonly state: ReportGeneratorStore,
    private readonly service: ReportGeneratorService,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly sessionService: SessionService,
    private mixpanelService: MixpanelService
  ) {}

  trackByPageId = (index: number, page: LibraryPage) => `${this.state.currentTemplateId}-${page.unique_template_key}`;

  ngOnInit(): void {
    const testIdParam = this.route.snapshot.queryParams['testId'];
    const templateIdParam = this.route.snapshot.queryParams['templateId'];
    const testid = testIdParam ? +testIdParam : -1;
    // TODO: Default template should be set at the org level
    const localStorageActiveTemplate = localStorage.getItem(ActiveTemplateLocalStorageKey);
    const activeTemplateId = localStorageActiveTemplate ? +localStorageActiveTemplate : 1;
    const templateId = templateIdParam ? +templateIdParam : activeTemplateId;

    this.startMixpanelRecording();
    this.setupListeners();
    this.state.loadTestData(testid);
    this.state.getTemplate(templateId);
    this.state.initStore();

    if (this.shouldShowOldReportGenerator()) {
      this.state.fetchOldTemplates();
    }

    const localStorageZoom = localStorage.getItem(LOCAL_STORAGE_ZOOM_KEY);
    this.state.setZoom(localStorageZoom != null ? parseFloat(localStorageZoom) : DEFAULT_ZOOM);
  }

  ngAfterViewInit(): void {
    // Subscribe to changes in page wrappers (when pages are added/removed)
    this.pageWrappers.changes.subscribe(() => {
      // If we have a selected page, scroll to it after the view updates
      if (this.selectedPageIndex >= 0) {
        setTimeout(() => this.scrollToPage(this.selectedPageIndex), 100);
      }
    });
  }

  generatePdf = (): void => {
    let config: ReportConfig | null = null;
    this.config$.pipe(take(1)).subscribe((value) => (config = value));

    if (config == null) {
      console.log('Report template not loaded');
      return;
    }

    const pageElements = this.pageWrappers
      .toArray()
      .map((ref) => ref.nativeElement.querySelector('.page-container') as HTMLElement);

    this.service.generateReport(config, pageElements).subscribe({
      next: (pdfBytes: Uint8Array) => {
        console.log('PDF generated successfully, size:', pdfBytes.length, 'bytes');

        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        (pdfBytes as any) = null; // release the Uint8Array immediately

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.getReportFileName(config!);
        link.click();

        // Clean up
        window.URL.revokeObjectURL(url);

        console.log('PDF download initiated');
      },
      error: (error) => {
        console.error('Error generating PDF:', error);
      },
      complete: () => {
        config = null;
        console.log('PDF generation completed');
      },
    });
  };

  configurePdf = (): void => {
    this.isConfiguring.update((v) => !v);
  };

  onZoomChange(newZoom: number): void {
    this.state.setZoom(newZoom);
  }

  disableZoom(currentZoom: number, direction: 'up' | 'down'): boolean {
    if (direction === 'up') {
      return currentZoom > 1.49;
    }
    if (direction === 'down') {
      return currentZoom < 1.01;
    }
    return false;
  }

  zoomIn(currentZoom: number): void {
    if (currentZoom === 1.5) {
      return;
    }

    this.state.setZoom(currentZoom + 0.1);
  }

  zoomOut(currentZoom: number): void {
    if (currentZoom === 1) {
      return;
    }

    this.state.setZoom(currentZoom - 0.1);
  }

  getPagesContainerStyle(): { [key: string]: string } {
    return {
      width: '100%',
      'max-width': '1800px',
      'min-width': '480px',
    };
  }

  getDropZoneWidth(): string {
    const width = PortaitPreview.width - 6;
    return `${width}px`;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'copy';
  }

  onDrop(event: DragEvent, insertIndex: number): void {
    event.preventDefault();
    (event.target as HTMLElement).classList.remove('drag-over');

    const pageData = event.dataTransfer?.getData('application/json');
    if (pageData) {
      const page = JSON.parse(pageData) as LibraryPage;
      this.state.insertPageAtIndex(page, insertIndex);

      // Scroll to the newly inserted page after a brief delay to let the DOM update
      setTimeout(() => {
        this.selectedPageIndex = insertIndex;
        this.scrollToPage(insertIndex);
      }, 150);
    }
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    (event.target as HTMLElement).classList.add('drag-over');
  }

  onDragLeave(event: DragEvent): void {
    (event.target as HTMLElement).classList.remove('drag-over');
  }

  onPageReordered(event: { fromIndex: number; toIndex: number }): void {
    this.state.reorderPages(event.fromIndex, event.toIndex);
  }

  onPageSelected(index: number): void {
    this.selectedPageIndex = index;
    this.scrollToPage(index);
  }

  deletePage(pageIndex: number): void {
    const message = 'Are you sure you want to remove this page from the report?';
    const dialogData: ConfirmDialogModel = new ConfirmDialogModel('Confirm page removal', message, 'Remove', 'Cancel');
    const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((res): void => {
      if (res === true) {
        this.state.removePageAtIndex(pageIndex);
      }
    });
  }

  onInsertPage(pageLibrary: LibraryPage[], reportConfig: ReportConfig, insertIndex: number): void {
    const dialogRef: MatDialogRef<AddPageDialogComponent> = this.dialog.open(AddPageDialogComponent, {
      width: '960px',
      disableClose: false,
      panelClass: 'general-dialog',
      data: {
        pageLibrary: pageLibrary,
        reportConfig: reportConfig,
      },
    });

    dialogRef.afterClosed().subscribe((pageId: number | undefined) => {
      if (pageId) {
        const libraryPage = pageLibrary.find((p) => p.id === pageId);
        if (libraryPage) {
          this.state.insertPageAtIndex(libraryPage, insertIndex);
        }
      }
    });
  }

  extractColorsFromLogo = this.service.extractColorsFromOrgLogo.bind(this.service);

  private setupListeners(): void {
    this.config$ = this.state.config$;
    this.templates$ = this.state.templates$;
    this.pageLibrary$ = this.state.pageLibrary$;
  }

  private scrollToPage(index: number): void {
    if (!this.centerColumn || !this.pageWrappers) {
      return;
    }

    const pageWrappersArray = this.pageWrappers.toArray();
    if (index < 0 || index >= pageWrappersArray.length) {
      return;
    }

    const pageElement = pageWrappersArray[index].nativeElement as HTMLElement;
    const centerColumnElement = this.centerColumn.nativeElement as HTMLElement;

    const pageTop = pageElement.offsetTop;
    const pageHeight = pageElement.offsetHeight;
    const centerColumnHeight = centerColumnElement.clientHeight;

    const scrollTo = pageTop - centerColumnHeight / 2 + pageHeight / 2;

    centerColumnElement.scrollTo({
      top: scrollTo,
      behavior: 'smooth',
    });
  }

  private startMixpanelRecording() {
    try {
      this.mixpanelService.start_session_recording();
    } catch (e) {
      console.error(e);
    }
  }

  private getReportFileName(config: ReportConfig): string {
    const athleteFirstName = config.test?.athlete?.first_name || 'Athlete';
    const athleteLastName = config.test?.athlete?.last_name || '';
    const athleteName = `${athleteFirstName} ${athleteLastName}`.trim();
    const templateName = config.template?.name || 'Report';

    return `${athleteName} - ${templateName}.pdf`;
  }

  private shouldShowOldReportGenerator() {
    const auth: Record<string, any> = JSON.parse(this.sessionService.getCookie('auth') || '{}');
    const date_joined = auth.date_joined;
    return date_joined != null ? date_joined < '2026-04-01T00:00:00Z' : false;
  }
}
